import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  IconButton,
  Collapse,
  Divider,
  Stack,
  Chip,
  Container,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import PsychologyIcon from '@mui/icons-material/Psychology';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';

const PRIMARY = '#6366f1';

function WorkoutHistoryPage() {
  const navigate = useNavigate();
  const [workouts, setWorkouts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingWorkout, setEditingWorkout] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [expandedDates, setExpandedDates] = useState({});
  const [showInsights, setShowInsights] = useState(false);
  const [insights, setInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);

  useEffect(() => {
    fetchWorkouts();
  }, []);

  const fetchWorkouts = async () => {
    try {
      const response = await api.get('/api/workouts');

      const grouped = {};
      response.data.workouts.forEach((workout) => {
        if (!grouped[workout.exercise]) grouped[workout.exercise] = {};
        if (!grouped[workout.exercise][workout.date])
          grouped[workout.exercise][workout.date] = [];
        grouped[workout.exercise][workout.date].push(workout);
      });

      Object.keys(grouped).forEach((exercise) => {
        const dates = Object.keys(grouped[exercise]).sort(
          (a, b) => new Date(b) - new Date(a)
        );
        const last5Dates = dates.slice(0, 5);
        const filteredDates = {};
        last5Dates.forEach((date) => {
          filteredDates[date] = grouped[exercise][date];
        });
        grouped[exercise] = filteredDates;
      });

      setWorkouts(grouped);
    } catch (err) {
      setError(
        'Error loading workout history: ' +
          (err.response?.data?.detail || err.message)
      );
    } finally {
      setLoading(false);
    }
  };

  const parseDate = d => {
    if (!d) return new Date(NaN);
    // M/D/YY  e.g. "3/7/26"
    if (/^\d{1,2}\/\d{1,2}\/\d{2}$/.test(d)) {
      const [m, day, yr] = d.split('/');
      return new Date(2000 + parseInt(yr), parseInt(m) - 1, parseInt(day));
    }
    // YYYY-MM-DD or ISO with time
    return new Date(d.slice(0, 10) + 'T00:00:00');
  };

  const formatDate = (dateString) => {
    const date = parseDate(dateString);
    if (isNaN(date)) return dateString || '—';
    return date.toLocaleDateString('en-SG', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const toggleDate = (exercise, date) => {
    const key = `${exercise}-${date}`;
    setExpandedDates((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const isDateExpanded = (exercise, date) => {
    const key = `${exercise}-${date}`;
    return expandedDates[key] || false;
  };

  const handleEdit = (workout, e) => {
    if (e) e.stopPropagation();
    console.log('Editing workout:', workout.id, workout);
    console.log('Current editingWorkout:', editingWorkout);
    setEditingWorkout(workout.id);
    setEditFormData({
      date: workout.date,
      exercise: workout.exercise,
      load_kg: workout.load_kg,
      reps: workout.reps,
      sets: workout.sets,
      notes: workout.notes || '',
    });
  };

  const handleCancelEdit = () => {
    setEditingWorkout(null);
    setEditFormData({});
  };

  const handleEditChange = (e) => {
    setEditFormData({ ...editFormData, [e.target.name]: e.target.value });
  };

  const handleSaveEdit = async (workoutId) => {
    try {
      await api.put(`/api/workouts/${workoutId}`, editFormData);
      setEditingWorkout(null);
      setEditFormData({});
      await fetchWorkouts();
    } catch (err) {
      alert('Error updating workout: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (workoutId) => {
    if (!window.confirm('Are you sure you want to delete this workout?')) return;
    try {
      await api.delete(`/api/workouts/${workoutId}`);
      await fetchWorkouts();
    } catch (err) {
      alert('Error deleting workout: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDownloadCSV = async () => {
    try {
      const response = await api.get('/api/workouts');
      const allWorkouts = response.data.workouts;
      if (allWorkouts.length === 0) {
        alert('No workouts to download');
        return;
      }
      const headers = ['Date', 'Exercise', 'Load (kg)', 'Reps', 'Sets', 'Volume (kg)', 'Notes'];
      const csvRows = [headers.join(',')];
      allWorkouts.forEach((workout) => {
        const volume = workout.load_kg * workout.reps * workout.sets;
        const row = [
          workout.date,
          `"${workout.exercise}"`,
          workout.load_kg,
          workout.reps,
          workout.sets,
          volume.toFixed(0),
          `"${workout.notes || ''}"`,
        ];
        csvRows.push(row.join(','));
      });
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      const today = new Date().toISOString().split('T')[0];
      const filename = `lifehub-workouts-${today}.csv`;
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      alert('Error downloading CSV: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleGenerateInsights = async () => {
    setLoadingInsights(true);
    setShowInsights(true);
    setInsights(null);
    try {
      const response = await api.post('/api/workouts/insights');
      console.log('Insights response:', response.data);
      setInsights(response.data.insights);
    } catch (err) {
      console.error('Insights error:', err);
      setInsights({
        overview: 'Unable to generate insights at this time.',
        insights: [],
        recommendations: [],
        error: err.response?.data?.detail || err.message,
      });
    } finally {
      setLoadingInsights(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress sx={{ color: PRIMARY }} />
          <Typography color="text.secondary">Loading workout history...</Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
        <Container maxWidth="lg">
          <Alert severity="error">{error}</Alert>
        </Container>
      </Box>
    );
  }

  const exerciseCount = Object.keys(workouts).length;

  // Inline edit row component
  const EditRow = ({ workout, onSave, onCancel }) => (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      alignItems={{ sm: 'center' }}
      sx={{ p: 1.5, bgcolor: 'rgba(99,102,241,0.1)', borderRadius: 1 }}
    >
      <TextField
        name="load_kg"
        label="Load (kg)"
        type="number"
        value={editFormData.load_kg}
        onChange={handleEditChange}
        inputProps={{ step: 0.5 }}
        size="small"
        sx={{ width: 110 }}
      />
      <TextField
        name="reps"
        label="Reps"
        type="number"
        value={editFormData.reps}
        onChange={handleEditChange}
        size="small"
        sx={{ width: 90 }}
      />
      <TextField
        name="sets"
        label="Sets"
        type="number"
        value={editFormData.sets}
        onChange={handleEditChange}
        size="small"
        sx={{ width: 90 }}
      />
      <TextField
        name="notes"
        label="Notes"
        value={editFormData.notes}
        onChange={handleEditChange}
        size="small"
        sx={{ flex: 1 }}
      />
      <Stack direction="row" spacing={0.5}>
        <IconButton
          onClick={() => onSave(workout.id)}
          size="small"
          sx={{ color: 'success.main', '&:hover': { bgcolor: 'rgba(34,197,94,0.15)' } }}
          title="Save"
        >
          <CheckIcon />
        </IconButton>
        <IconButton
          onClick={onCancel}
          size="small"
          sx={{ color: 'error.main', '&:hover': { bgcolor: 'rgba(239,68,68,0.12)' } }}
          title="Cancel"
        >
          <CloseIcon />
        </IconButton>
      </Stack>
    </Stack>
  );

  const fmtShortDate = d => parseDate(d).toLocaleDateString('en-SG', { day: 'numeric', month: 'short' });

  // Single workout display row — compact single-line for mobile
  const WorkoutRow = ({ workout, date, showDate = true }) => {
    const isEditing = editingWorkout === workout.id;

    if (isEditing) {
      return <EditRow workout={workout} onSave={handleSaveEdit} onCancel={handleCancelEdit} />;
    }

    return (
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 },
        py: 0.75, px: 1.25, borderRadius: 1,
        borderBottom: '1px solid rgba(255,255,255,0.04)',
        '&:hover': { bgcolor: 'rgba(99,102,241,0.08)' },
      }}>
        {showDate && (
          <Typography sx={{ fontSize: '0.75rem', color: 'var(--tx-hi)', width: 58, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
            {fmtShortDate(date)}
          </Typography>
        )}
        <Chip label={`${workout.load_kg} kg`} size="small"
          sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, bgcolor: 'rgba(99,102,241,0.2)', color: PRIMARY, '& .MuiChip-label': { px: 0.75 } }} />
        <Typography sx={{ fontSize: '0.75rem', color: 'var(--tx-lo)', flex: 1 }}>
          {workout.sets}×{workout.reps} reps
        </Typography>
        {workout.notes && (
          <Typography sx={{ fontSize: '0.7rem', color: 'var(--tx-gh)', fontStyle: 'italic', maxWidth: { xs: 80, sm: 160 }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: { xs: 'none', sm: 'block' } }}>
            {workout.notes}
          </Typography>
        )}
        <Stack direction="row" spacing={0} sx={{ flexShrink: 0 }}>
          <IconButton size="small" onClick={e => handleEdit(workout, e)}
            sx={{ p: 0.5, color: 'var(--tx-dim)', '&:hover': { color: PRIMARY, bgcolor: 'rgba(99,102,241,0.12)' } }}>
            <EditIcon sx={{ fontSize: 15 }} />
          </IconButton>
          <IconButton size="small" onClick={() => handleDelete(workout.id)}
            sx={{ p: 0.5, color: 'var(--tx-dim)', '&:hover': { color: '#ef4444', bgcolor: 'rgba(239,68,68,0.1)' } }}>
            <DeleteIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Stack>
      </Box>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: { xs: 2, sm: 4 } }}>
      <Container maxWidth="md" sx={{ px: { xs: 1.5, sm: 3 } }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5} flexWrap="wrap" gap={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FitnessCenterIcon sx={{ color: PRIMARY, fontSize: 24 }} />
            <Typography variant="h6" fontWeight={700} color="text.primary">Workout History</Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <IconButton onClick={handleDownloadCSV} title="Download CSV"
              sx={{ color: PRIMARY, border: `1px solid ${PRIMARY}44`, borderRadius: 1.5, p: 0.75, '&:hover': { bgcolor: `${PRIMARY}15` } }}>
              <DownloadIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/workout/log')} size="small"
              sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#4f46e5' }, fontSize: '0.8rem' }}>
              Log Workout
            </Button>
          </Stack>
        </Stack>

        {exerciseCount === 0 ? (
          <Paper
            elevation={2}
            sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}
          >
            <Typography color="text.secondary" mb={2}>
              No workouts logged yet.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/workout/log')}
              sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#4f46e5' } }}
            >
              Log Your First Workout
            </Button>
          </Paper>
        ) : (
          <>
            {/* Summary */}
            <Alert
              severity="info"
              sx={{ mb: 2, borderLeft: `4px solid ${PRIMARY}`, bgcolor: 'rgba(99,102,241,0.1)' }}
            >
              Tracking <strong>{exerciseCount}</strong> exercise
              {exerciseCount !== 1 ? 's' : ''}
            </Alert>

            {/* AI Insights Section */}
            <Paper elevation={0} sx={{ mb: 3, boxShadow: '0 0 0 1px rgba(255,255,255,0.05), 0 2px 8px rgba(0,0,0,0.3)', borderRadius: 3, overflow: 'hidden' }}>
              <Button
                fullWidth
                disabled={loadingInsights}
                onClick={handleGenerateInsights}
                startIcon={loadingInsights ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <PsychologyIcon />}
                sx={{
                  py: 1.75,
                  background: `linear-gradient(135deg, ${PRIMARY} 0%, #764ba2 100%)`,
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '1rem',
                  borderRadius: 0,
                  '&:hover': {
                    background: `linear-gradient(135deg, #4f46e5 0%, #6b3f9e 100%)`,
                  },
                  '&:disabled': { opacity: 0.7, color: 'white' },
                }}
              >
                {loadingInsights ? 'Analyzing...' : 'Generate AI Insights'}
              </Button>

              {showInsights && (
                <Box sx={{ p: 3 }}>
                  {loadingInsights ? (
                    <Stack alignItems="center" spacing={1} py={2}>
                      <CircularProgress sx={{ color: PRIMARY }} size={28} />
                      <Typography color="text.secondary">Analyzing your workout data...</Typography>
                    </Stack>
                  ) : insights ? (
                    <Box>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6" fontWeight={700} color="text.primary">
                          Your Workout Insights
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => setShowInsights(false)}
                          sx={{ color: 'var(--tx-lo)', '&:hover': { color: '#dc2626' } }}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Stack>

                      {insights.summary && (
                        <Stack
                          direction="row"
                          spacing={2}
                          mb={2}
                          sx={{ p: 2, bgcolor: 'rgba(99,102,241,0.1)', borderRadius: 2 }}
                        >
                          {[
                            { value: insights.summary.session_count, label: 'Sessions' },
                            { value: insights.summary.exercise_count, label: 'Exercises' },
                            {
                              value: insights.summary.total_volume_kg.toLocaleString(),
                              label: 'Total Volume (kg)',
                            },
                          ].map((stat) => (
                            <Box key={stat.label} sx={{ flex: 1, textAlign: 'center' }}>
                              <Typography variant="h5" fontWeight={700} color={PRIMARY}>
                                {stat.value}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {stat.label}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      )}

                      <Box
                        sx={{
                          p: 2,
                          mb: 2,
                          bgcolor: 'rgba(255,255,255,0.04)',
                          borderLeft: `4px solid ${PRIMARY}`,
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" color="text.primary" lineHeight={1.6}>
                          {insights.overview}
                        </Typography>
                      </Box>

                      {insights.insights && insights.insights.length > 0 && (
                        <Box mb={2}>
                          <Typography variant="subtitle2" fontWeight={700} color="text.primary" mb={1}>
                            Key Observations
                          </Typography>
                          <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                            {insights.insights.map((insight, idx) => (
                              <Typography
                                key={idx}
                                component="li"
                                variant="body2"
                                color="text.secondary"
                                lineHeight={1.6}
                                mb={0.75}
                              >
                                {insight}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      )}

                      {insights.recommendations && insights.recommendations.length > 0 && (
                        <Box
                          sx={{
                            p: 2,
                            bgcolor: 'rgba(34,197,94,0.08)',
                            borderLeft: '4px solid #16a34a',
                            borderRadius: 1,
                            mb: 2,
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight={700} color="success.light" mb={1}>
                            Recommendations
                          </Typography>
                          <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                            {insights.recommendations.map((rec, idx) => (
                              <Typography
                                key={idx}
                                component="li"
                                variant="body2"
                                color="success.main"
                                lineHeight={1.6}
                                mb={0.75}
                                fontWeight={500}
                              >
                                {rec}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      )}

                      {insights.error && (
                        <Alert severity="warning">{insights.error}</Alert>
                      )}
                    </Box>
                  ) : null}
                </Box>
              )}
            </Paper>

            {/* Exercise sections */}
            <Stack spacing={3}>
              {Object.entries(workouts).map(([exercise, dateGroups]) => {
                const dateCount = Object.keys(dateGroups).length;

                return (
                  <Card key={exercise} elevation={2} sx={{ borderRadius: 3, overflow: 'visible' }}>
                    <CardContent sx={{ p: 3 }}>
                      {/* Exercise header */}
                      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                        <Typography variant="h6" fontWeight={700} color="text.primary">
                          {exercise}
                        </Typography>
                        <Chip
                          label={`Last ${dateCount} session${dateCount !== 1 ? 's' : ''}`}
                          size="small"
                          sx={{ bgcolor: 'rgba(99,102,241,0.2)', color: PRIMARY, fontSize: '0.75rem' }}
                        />
                      </Stack>
                      <Divider sx={{ mb: 2 }} />

                      {/* Date groups */}
                      <Stack spacing={1}>
                        {Object.entries(dateGroups)
                          .sort(([a], [b]) => new Date(b) - new Date(a))
                          .map(([date, sets]) => {
                            const isSingleSet = sets.length === 1;

                            if (isSingleSet) {
                              return (
                                <WorkoutRow
                                  key={sets[0].id}
                                  workout={sets[0]}
                                  date={date}
                                  showDate={true}
                                />
                              );
                            }

                            // Multiple workouts on same date — collapsible
                            const expanded = isDateExpanded(exercise, date);
                            const totalVolume = sets.reduce(
                              (sum, s) => sum + s.load_kg * s.reps * s.sets,
                              0
                            );

                            return (
                              <Box key={date}>
                                {/* Collapsible header */}
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  onClick={() => toggleDate(exercise, date)}
                                  sx={{
                                    py: 1,
                                    px: 1.5,
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    bgcolor: 'rgba(255,255,255,0.03)',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                                  }}
                                >
                                  <IconButton size="small" sx={{ color: PRIMARY, mr: 0.5, p: 0.25 }}>
                                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                  </IconButton>
                                  <Typography variant="body2" color="text.secondary" sx={{ width: 100, flexShrink: 0 }}>
                                    {formatDate(date)}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    {sets.length} entries &bull; Total volume: {totalVolume.toFixed(0)} kg
                                  </Typography>
                                </Stack>

                                <Collapse in={expanded}>
                                  <Stack spacing={0.5} sx={{ pl: 2, mt: 0.5 }}>
                                    {sets.map((workout) => (
                                      <WorkoutRow
                                        key={workout.id}
                                        workout={workout}
                                        date={date}
                                        showDate={false}
                                      />
                                    ))}
                                  </Stack>
                                </Collapse>
                              </Box>
                            );
                          })}
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          </>
        )}
      </Container>
    </Box>
  );
}

export default WorkoutHistoryPage;
